/* eslint-disable no-underscore-dangle */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import moment from 'moment-timezone';
import * as TimeZoneData from 'countries-and-timezones';
import logger from './logger';
import { createLocalStorage } from './storage';
import { isNotEmpty } from './utilities';

dayjs.extend(utc);
dayjs.extend(timezone);
export const offsetToTimezone = {
  PST: 'America/Los_Angeles',
  PDT: 'America/Los_Angeles',
  MST: 'America/Chicago',
  MDT: 'America/Chicago',
  EST: 'America/New_York',
  EDT: 'America/New_York',
  CST: 'America/Indiana/Knox',
  CDT: 'America/Indiana/Knox',
  GMT: 'Europe/London',
  // Colon-formatted versions for consistency with initializeCountries
  'GMT-11': 'Pacific/Pago_Pago',
  'GMT-10': 'Pacific/Honolulu',
  'GMT-9': 'America/Anchorage',
  'GMT-8': 'America/Los_Angeles',
  'GMT-7': 'America/Denver',
  'GMT-6': 'America/Chicago',
  'GMT-5': 'America/New_York',
  'GMT-4': 'America/Caracas',
  'GMT-3': 'America/Argentina/Buenos_Aires',
  'GMT-2': 'Atlantic/South_Georgia',
  'GMT-1': 'Atlantic/Azores',
  'GMT+0': 'Europe/London',
  'GMT+1': 'Europe/Paris',
  'GMT+2': 'Europe/Athens',
  'GMT+3': 'Asia/Riyadh',
  'GMT+4': 'Asia/Dubai',
  'GMT+5': 'Asia/Karachi',
  'GMT+6': 'Asia/Dhaka',
  'GMT+7': 'Asia/Jakarta',
  'GMT+8': 'Asia/Shanghai',
  'GMT+9': 'Asia/Tokyo',
  'GMT+10': 'Australia/Sydney',
  'GMT+11': 'Pacific/Noumea',
  'GMT+12': 'Pacific/Fiji',
  // Non-hour offsets (30-minute and 45-minute intervals)
  'GMT-9:30': 'Pacific/Marquesas',
  'GMT-3:30': 'America/St_Johns',
  'GMT+3:30': 'Asia/Tehran',
  'GMT+4:30': 'Asia/Kabul',
  'GMT+5:30': 'Asia/Kolkata',
  'GMT+5:45': 'Asia/Kathmandu',
  'GMT+6:30': 'Asia/Yangon',
  'GMT+8:45': 'Australia/Eucla',
  'GMT+9:30': 'Australia/Adelaide',
  'GMT+10:30': 'Australia/Lord_Howe',
  'GMT+12:45': 'Pacific/Chatham',
  'GMT+13:00': 'Pacific/Kanton',
  'GMT+14:00': 'Pacific/Kiritimati',
  // Add more mappings as needed
};

export const getCurrentYear = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  return currentYear;
};

export const isValidTimezone = (tz) => !!moment.tz.zone(tz);
export const getUserTimezone = () => {
  let userTimezone = '';
  const storage = createLocalStorage();
  const storageTimezone = storage.getItem('userPreferredTimezone');

  if (storageTimezone !== null) {
    userTimezone = storageTimezone;
  } else if (moment?.tz?.guess) {
    userTimezone = moment.tz.guess();
  } else if (typeof Intl === 'object' && typeof Intl.DateTimeFormat === 'function') {
    userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  if (isNotEmpty(userTimezone) && isValidTimezone(userTimezone)) {
    return userTimezone;
  }

  return 'America/Los_Angeles';
};

export const getUser24Hour = () => {
  const storage = createLocalStorage();
  const storage24Hour = storage.getItem('userPreferred24Hour');

  if (storage24Hour !== null) {
    return storage24Hour;
  }
  return false;
};

/**
 * Returns the timezone abbreviation for a given date and preferred timezone.
 *
 * @param {string} date - The date string in 'MMM DD, YYYY' format (e.g., 'Oct 31, 2025').
 * @param {object|string} preferredTimezone - The user's preferred timezone object or string.
 * @returns {string} The timezone abbreviation (e.g., 'PST', 'EDT', 'GMT+02').
 */
export const getTimezoneAbbreviation = (date, preferredTimezone) => {
  if (!date || !preferredTimezone) {
    return '';
  }

  const tzName = preferredTimezone?.name || preferredTimezone;
  if (!tzName) {
    return '';
  }

  const m = moment.tz(date, 'MMM DD, YYYY', tzName);
  if (!m.isValid()) {
    return '';
  }

  let tzAbbr = m.zoneAbbr ? m.zoneAbbr() : m.format('z');

  // If the abbreviation looks like a numeric offset (e.g., '+02'), prepend 'GMT'
  if (/^[+-]\d{2}/.test(tzAbbr)) {
    tzAbbr = `GMT${tzAbbr}`;
  }

  return tzAbbr;
};

export const formatCustomerAvailability = (dates) => {
  if (!Array.isArray(dates)) return [];
  const dateArr = dates.map((dateTimeStr) => {
    const parts = dateTimeStr.split(' ');
    const date = parts.slice(0, 3).join(' ');
    const timezonePart = offsetToTimezone[parts.slice(-1)[0]];
    const timerange = parts.slice(3, -1).join(' ');
    const [start, end] = timerange.split(' - ');
    const reformattedStartTime = `${date} ${start}`;
    const reformattedEndTime = `${date} ${end}`;
    const startTime = moment.tz(reformattedStartTime, 'MMM D, YYYY h:mm A', timezonePart);
    const endTime = moment.tz(reformattedEndTime, 'MMM D, YYYY h:mm A', timezonePart);
    return {
      startTime,
      endTime,
    };
  });
  return dateArr;
};

export const formatDeliveryInfo = (startDateStr, endDateStr) => {
  const dateArr = {
    startTime: moment.utc(startDateStr),
    endTime: moment.utc(endDateStr),
  };
  return dateArr;
};

const convertToPreferredTimezone = (dateTimeStr, userTimezone, userPreferred24Hour) => {
  let format = 'h:mm A';
  if (userPreferred24Hour) {
    format = 'H:mm';
  }
  return {
    date: moment(dateTimeStr.startTime)?.clone().tz(userTimezone)?.format('MMM DD, YYYY'),
    startTime: moment(dateTimeStr.startTime)?.clone().tz(userTimezone)?.format(format),
    endTime: moment(dateTimeStr.endTime)?.clone().tz(userTimezone)?.format(format),
  };
};

export const convertStringDatesToDates = ({ dates, preferredTimezone, preferred24Hour }) => {
  const arr = dates || [];
  const convertedDateTimes = arr.map((ds) => convertToPreferredTimezone(ds, preferredTimezone.name, preferred24Hour));
  const groupedByDate = convertedDateTimes.reduce((acc, { date, startTime, endTime }) => {
    const accDate = acc;
    if (date) {
      if (!accDate[date]) {
        accDate[date] = [{ startTime, endTime }];
      } else {
        accDate[date].push({ startTime, endTime });
      }
    }
    return accDate;
  }, {});
  return groupedByDate;
};

/**
 * Function expects an array of dates appearing like:
 * [
 *     "Mar 28, 2024 5:00 AM - 6:00 AM GMT+11",
 *     "Mar 28, 2024 14:00 - 15:00 PST",
 *     "Mar 28, 2024 3:00 PM - 4:00 PM EST",
 *     "Mar 22, 2025 4:00 AM - 5:00 AM GMT"
 * ]
 * and will convert to an array of javascript Date objects
 * using the conversion table offsetToTimezone to look up
 * a timezone name to suit the GMT offset or timezone abbr.
 *
 * @param dates
 * @param preferredTimezone
 * @returns {{date: *, startTime: *, endTime: *}|{date: *}}
 */
export const convertArrayStringDatesToDates = ({ dates }) => {
  const arr = dates || [];
  const arrDates = arr.map((str) => {
    const parts = str.split(' ');
    const date = parts.slice(0, 3).join(' ');
    const timezonePart = offsetToTimezone[parts.slice(-1)[0]];
    const timerange = parts.slice(3, -1).join(' ');

    if (timezonePart) {
      const [startTime, endTime] = timerange.split(' - ');
      let returnStart;
      let returnEnd;
      try {
        const reformattedStartTime = `${date} ${startTime}`;
        const reformattedEndTime = `${date} ${endTime}`;
        const startTimeUtc = moment.utc(reformattedStartTime, 'MMM D, YYYY h:mm A', timezonePart);
        const endTimeUtc = moment.utc(reformattedEndTime, 'MMM D, YYYY h:mm A', timezonePart);
        returnStart = startTimeUtc;
        returnEnd = endTimeUtc;
      } catch (e) {
        logger.error('Error setting times', e);
        returnStart = new Date('Invalid date');
        returnEnd = new Date('Invalid date');
      }
      return { date, startTime: returnStart, endTime: returnEnd };
    }
    logger.error('Error setting times, no dates passed in', dates);
    return { date: new Date('Invalid date') };
  });

  const groupedByDate = arrDates.reduce((acc, { date, startTime, endTime }) => {
    const accDate = acc;
    if (date) {
      if (!accDate[date]) {
        accDate[date] = [{ startTime, endTime }];
      } else {
        accDate[date].push({ startTime, endTime });
      }
    }
    return accDate;
  }, {});
  return groupedByDate;
};

export const formatDayjsObjForDateDisplay = ({ dateObject, userPreferredTimezone }) => {
  const format = 'MMM DD, YYYY';
  return moment(dateObject.toDate())
    .tz(userPreferredTimezone.name)
    .format(format);
};

export const formatDayjsObjForTimeDisplay = ({ dateObject, userPreferredTimezone, userPreferred24Hour }) => {
  const userTimezone = userPreferredTimezone.name;
  const originalDate = moment.tz(dateObject._i, dateObject._f, dateObject._l);
  const userTimezoneMoment = originalDate.clone().tz(userTimezone);
  const localeTimeInUserTimezone = userTimezoneMoment.format('LT');
  if (userPreferred24Hour) {
    return userTimezoneMoment.format('H:mm');
  }
  return localeTimeInUserTimezone;
};

export const formatUtcDateStringToDateObjectTargetTimezone = ({ dateStringUTC, userPreferredTimezone }) => {
  const dateString = dateStringUTC;
  const dateUTC = moment.utc(dateString);
  const datePreferredTimezone = dateUTC.tz(userPreferredTimezone.name || userPreferredTimezone);
  return datePreferredTimezone;
};
export const formatUtcDateStringToDateDisplayTargetTimezone = ({
  dateStringUTC,
  userPreferredTimezone,
  userPreferred24Hour,
  withTzAbbr = false,
}) => {
  const dateString = dateStringUTC;
  const dateUTC = moment.utc(dateString);
  const datePreferredTimezone = dateUTC.tz(userPreferredTimezone.name || userPreferredTimezone);
  if (datePreferredTimezone !== undefined) {
    if (withTzAbbr) {
      // Format: "Aug 17, 2025 at 9:00 AM PST"
      const datePart = datePreferredTimezone.format('MMM DD, YYYY');
      const timePart = userPreferred24Hour
        ? datePreferredTimezone.format('H:mm')
        : datePreferredTimezone.format('h:mm A');
      // Get timezone abbreviation using getTimezoneAbbreviation
      const tzAbbr = getTimezoneAbbreviation(datePreferredTimezone, userPreferredTimezone.name || userPreferredTimezone);
      return `${datePart} at ${timePart} ${tzAbbr}`;
    }
    if (userPreferred24Hour) {
      return datePreferredTimezone?.format('MMM DD, YYYY H:mm');
    }
    return datePreferredTimezone?.format('MMM DD, YYYY h:mm A');
  }
  return '';
};

export const formatDateRange = (startDateStr, endDateStr) => {
  const startDate = moment.utc(startDateStr);
  const endDate = moment.utc(endDateStr);

  const formattedStartDate = startDate.tz('GMT')?.format('MMM D, YYYY h:mm A');
  const formattedEndDate = endDate.tz('GMT')?.format('h:mm A');

  const offset = startDate.utcOffset();
  const offsetHours = Math.floor(offset / 60);
  const offsetString = offsetHours >= 0 ? `GMT+${offsetHours}` : `GMT${offsetHours}`;

  return `${formattedStartDate} - ${formattedEndDate} ${offsetString}`;
};

export const initializeCountries = () => {
  const countries = TimeZoneData.getAllCountries();
  const groupedTimezones = [];
  // Object.values(countries).forEach((country) => {
  //   // groupedTimezones.push({ label: country.name, id: undefined, level: 0 });
  //   country.timezones.forEach((zone) => {
  //     const timeZoneData = TimeZoneData.getTimezone(zone);
  //     const offSet = getGMTOffset(zone);
  //     const label = `${zone.replace(/_/ig, ' ')} (${offSet.gmtSuffixLabel})`;
  //     console.log('final ', timeZoneData, label, zone);
  //     // groupedTimezones.push({
  //     //   ...timeZoneData, label, id: zone, level: 1, parentName: country.name, tzAbbr: getGMTOffset(zone).gmtPrefixLabel, value: label, currentOffset: getGMTOffset(zone).currentOffset,
  //     // });
  //   });
  // });
  Object.values(countries).forEach((country) => {
    groupedTimezones.push({ label: country.name, id: undefined, level: 0 });
    country.timezones.forEach((tz) => {
      // console.log('tz', tz);
      const zoneData = TimeZoneData.getTimezone(tz);
      // Use standard time offset for consistency with formatCustomerAvailability
      const gmtOffset = zoneData.utcOffsetStr;
      const label = `${tz} (GMT${gmtOffset})`;
      // const final = {
      //   ...zoneData, value: label, label: `${tz} (GMT${gmtOffset})`, id: tz, level: 0,
      // };
      groupedTimezones.push({
        ...zoneData, label, id: tz, level: 1, parentName: country.name, value: label,
      });
      // groupedTimezones.push({ ...final });
    });
  });
  return groupedTimezones;
};

export const formatDateTimeRange = (startDatetime, endDatetime, userPreferred24Hour = false) => {
  if (!startDatetime || !endDatetime) return '';

  const startDate = new Date(startDatetime);
  const endDate = new Date(endDatetime);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return '';

  const dateOptions = {
    month: 'short', // 3-letter month (Oct)
    day: 'numeric',
    year: 'numeric',
  };

  const timeOptions = userPreferred24Hour
    ? { hour: '2-digit', minute: '2-digit', hour12: false }
    : { hour: 'numeric', minute: '2-digit', hour12: true };

  const startDateFormatted = startDate.toLocaleDateString('en-US', dateOptions);
  const endDateFormatted = endDate.toLocaleDateString('en-US', dateOptions);

  const startTimeFormatted = startDate.toLocaleTimeString('en-US', timeOptions);
  const endTimeFormatted = endDate.toLocaleTimeString('en-US', timeOptions);

  // Check if same date
  const isSameDate = startDateFormatted === endDateFormatted;

  if (isSameDate) {
    // Same date: "Oct 31, 2025 from 12:00 AM - 1:00 AM"
    return `${startDateFormatted} from ${startTimeFormatted} - ${endTimeFormatted}`;
  }
  // Different dates: "Oct 31, 2025 12:00 AM - Nov 1, 2025 1:00 AM"
  return `${startDateFormatted} ${startTimeFormatted} - ${endDateFormatted} ${endTimeFormatted}`;
};
